// Libraries
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {useToast} from '@supermove/hooks';
import {colors, Typography} from '@supermove/styles';

// App
import Toast from '@shared/design/components/Toast';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const OptionButton = Styled.ButtonV2`
`;

const OptionLabel = Styled.Text<{
  vars: {
    isSelected: boolean;
  };
  color: string;
}>`
  ${({vars}) => (vars.isSelected ? Typography.Mobile.Label : Typography.Mobile.Body)}
  color: ${({color}) => color};
`;

const OptionDescription = Styled.Text<{
  disabled?: boolean;
  vars: {
    isSelected: boolean;
  };
  color: string;
}>`
${({vars}) => (vars.isSelected ? Typography.Mobile.MicroLabel : Typography.Mobile.Micro)}
  color: ${({disabled, color}) => (disabled ? colors.gray.tertiary : color)};
`;

const ActivityIndicator = Styled.Loading`
`;

const View = Styled.View`
`;

export interface OptionProps {
  handleAction?: () => void;
  isDisabled?: boolean;
  label: React.ReactNode;
  value: string;
  description?: string;
  color?: string;
  isLoading?: boolean;
  tooltip?: string;
  ActionComponent?: React.FC<any>;
  actionComponentProps?: Record<string, any>;
  rightComponent?: React.ReactNode;
}

interface DropdownSheetOptionProps {
  option: OptionProps;
  showDescriptionInOption?: boolean;
  isSelected?: boolean;
  isColorOptions?: boolean;
  handleOption?: () => void;
  style?: React.CSSProperties;
}

const PreventPropagationContainer = ({children}: {children?: React.ReactNode}) => {
  return (
    <View
      onStartShouldSetResponder={() => true}
      onTouchEnd={(e) => {
        e.stopPropagation();
      }}
    >
      {children}
    </View>
  );
};

const getOptionLabelColor = ({
  isDisabled,
  isLoading,
  isSelected,
}: {
  isDisabled?: boolean;
  isLoading?: boolean;
  isSelected?: boolean;
}) => {
  if (isDisabled || isLoading) {
    return colors.gray.tertiary;
  }
  if (isSelected) {
    return colors.blue.interactive;
  }
  return colors.gray.primary;
};

const DropdownSheetOption = ({
  option,
  style,
  showDescriptionInOption = false,
  isSelected = false,
  isColorOptions = false,
  handleOption = () => {},
}: DropdownSheetOptionProps) => {
  const {
    handleAction,
    isDisabled,
    label,
    value,
    description,
    color,
    isLoading,
    tooltip = '',
    ActionComponent,
    actionComponentProps,
    rightComponent,
  } = option;
  const {handleToast} = useToast({
    ToastComponent: Toast,
    message: tooltip,
  });

  return (
    <OptionButton
      onPress={() => {
        if (tooltip) {
          handleToast();
        }
        if (!isDisabled) {
          if (handleAction) {
            handleAction();
          } else {
            handleOption();
          }
        }
      }}
      disabled={(isDisabled && !tooltip) || isLoading}
      style={style}
    >
      <Space height={12} />
      <Row>
        {isLoading && (
          <React.Fragment>
            <ActivityIndicator size={'small'} color={colors.blue.interactive} />
            <Space width={8} />
          </React.Fragment>
        )}
        {isColorOptions && (
          <React.Fragment>
            <Icon source={Icon.Circle} size={8} color={value} />
            <Space width={8} />
          </React.Fragment>
        )}
        <OptionLabel
          vars={{isSelected}}
          color={
            color && !isDisabled && !isLoading
              ? color
              : getOptionLabelColor({isDisabled, isLoading, isSelected})
          }
        >
          {label}
        </OptionLabel>
        {isSelected && (
          <React.Fragment>
            <Space style={{flex: 1, minWidth: 8}} />
            {ActionComponent && (
              <React.Fragment>
                <PreventPropagationContainer>
                  <ActionComponent {...actionComponentProps} />
                </PreventPropagationContainer>
                <Space width={16} />
              </React.Fragment>
            )}
            <Icon
              source={Icon.CheckCircle}
              size={18}
              color={isDisabled ? colors.gray.tertiary : colors.blue.interactive}
            />
          </React.Fragment>
        )}
        {rightComponent && (
          <React.Fragment>
            <Space style={{flex: 1, minWidth: 8}} />
            {rightComponent}
          </React.Fragment>
        )}
      </Row>
      {showDescriptionInOption && (
        <React.Fragment>
          <Space height={4} />
          <OptionDescription
            disabled={isDisabled}
            vars={{isSelected}}
            color={isSelected ? colors.blue.interactive : colors.gray.secondary}
          >
            {description}
          </OptionDescription>
        </React.Fragment>
      )}
      <Space height={12} />
    </OptionButton>
  );
};

export default DropdownSheetOption;
